import React, { useEffect, useState, useCallback, useRef } from "react";
import DashboardHeader from "../layouts/dashboard_header";
import SideMenu from "../layouts/dashboard_menu";
import DashboardFooter from "../layouts/dashboard_footer";
import Canvas from "../layouts/dahboard_canvas";
import FacilityPartners from "./FacilityPartners";
import Swal from "sweetalert2";
import axios from "axios";
import { useAppName, useBaseUrl } from "../../utils/utils";
import { Link } from "react-router-dom";
import localforage from "localforage";
import Staff from "./Staff";

const ACCENT = '#0f766e';
const ACCENT_SOFT = 'rgba(15, 118, 110, 0.10)';

interface SwitchableFacility {
    id: number;
    name: string;
    email: string;
    phone?: string;
    town?: string;
    category?: string;
    active: boolean;
    staff_count: number;
    is_home_clinic: boolean;
    is_current: boolean;
    institution_name?: string;
}

const ClinicLauncher: React.FC = () => {
    const app_name = useAppName();
    const base_url = useBaseUrl();

    const [step, setStep] = useState(1);
    const [clinicName, setClinicName] = useState('');
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [switchableFacilities, setSwitchableFacilities] = useState<SwitchableFacility[]>([]);
    const [isInstitutionAdmin, setIsInstitutionAdmin] = useState(false);
    const [staffName, setStaffName] = useState('');
    const [institutionName, setInstitutionName] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [showSwitcher, setShowSwitcher] = useState(false);
    const [isSwitching, setIsSwitching] = useState(false);
    const switcherRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        document.title = `${app_name} - My Facility`;
        loadData();
    }, []);

    // Close switcher on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (switcherRef.current && !switcherRef.current.contains(e.target as Node)) {
                setShowSwitcher(false);
            }
        };
        if (showSwitcher) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showSwitcher]);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [name, cId, sId] = await Promise.all([
                localforage.getItem<string>('clinic_name'),
                localforage.getItem<number>('clinic_id'),
                localforage.getItem<number>('staff_id'),
            ]);
            setClinicName(name || 'My Facility');
            setClinicId(cId || null);

            // Fetch switchable facilities
            if (sId) {
                try {
                    const r = await axios.post(`${base_url}facilities/clinics/switchable`, {
                        staff_id: sId,
                        clinic_id: cId,
                    }, { timeout: 10000 });
                    if (r.data?.status === 'success') {
                        const d = r.data.data;
                        setSwitchableFacilities(d.facilities || []);
                        setIsInstitutionAdmin(d.is_institution_admin || false);
                        setStaffName(d.staff_name || '');
                        setInstitutionName(d.institution_name || '');
                    }
                } catch (e) {
                    console.error('Failed to load switchable facilities:', e);
                    // Fallback to basic clinics/all endpoint
                    try {
                        const fallback = await axios.get(`${base_url}facilities/clinics/all`, { timeout: 10000 });
                        const fd = fallback.data;
                        if (fd?.status === 'success' && Array.isArray(fd.data)) {
                            setSwitchableFacilities(fd.data.map((f: any) => ({
                                ...f, staff_count: 0, is_home_clinic: f.id === cId, is_current: f.id === cId
                            })));
                        }
                    } catch { }
                }
            }
        } catch (e) {
            console.error('Failed to load facility data:', e);
        } finally {
            setIsLoading(false);
        }
    }, [base_url]);

    const switchFacility = async (fac: SwitchableFacility) => {
        if (fac.is_current) { setShowSwitcher(false); return; }
        const result = await Swal.fire({
            title: 'Switch Facility',
            html: `<div style="text-align:left;font-size:0.88rem;">
                <p>Switch your active dashboard to:</p>
                <div style="padding:12px;border-radius:10px;background:${ACCENT_SOFT};margin:10px 0;">
                    <div style="font-weight:700;color:${ACCENT}">${fac.name}</div>
                    <div style="font-size:0.76rem;color:#64748b">${fac.email} • ${fac.town || ''}</div>
                </div>
                <p style="font-size:0.78rem;color:#64748b">All dashboard data will reload for the selected facility.</p>
            </div>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: ACCENT,
            confirmButtonText: 'Switch Now',
            cancelButtonText: 'Cancel',
        });
        if (!result.isConfirmed) return;

        setIsSwitching(true);
        try {
            await localforage.setItem('clinic_id', fac.id);
            await localforage.setItem('clinic_name', fac.name);
            if (fac.email) await localforage.setItem('clinic_email', fac.email);

            setClinicId(fac.id);
            setClinicName(fac.name);
            setShowSwitcher(false);

            // Reload switchable to update is_current flags
            setSwitchableFacilities(prev => prev.map(f => ({ ...f, is_current: f.id === fac.id })));

            await Swal.fire({
                icon: 'success', title: 'Switched!',
                text: `Now operating as "${fac.name}"`,
                timer: 1800, showConfirmButton: false,
            });

            // Reload page to refresh all dashboard data
            window.location.reload();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to switch facility.' });
        } finally { setIsSwitching(false); }
    };

    const modules = [
        { key: 2, label: 'My Partners', desc: 'Find and manage partnered facilities', icon: 'bx bx-group', color: ACCENT },
        { key: 3, label: 'My Services', desc: 'View and manage offered services', icon: 'bx bx-briefcase-alt-2', color: '#3b82f6' },
        { key: 4, label: 'My Staff', desc: 'Manage staff members and roles', icon: 'bx bx-user-plus', color: '#8b5cf6' },
        { key: 5, label: 'My Contracts', desc: 'Review active partnership contracts', icon: 'bx bx-file', color: '#d97706' },
        { key: 6, label: 'Partners Directory', desc: 'Explore the partner directory', icon: 'bx bx-globe', color: '#e11d48' },
    ];

    const sections = [
        { key: 1, label: 'Home', icon: 'bx bx-home-alt' },
        { key: 2, label: 'Partners', icon: 'bx bx-group' },
        { key: 3, label: 'Services', icon: 'bx bx-briefcase-alt-2' },
        { key: 4, label: 'Staff', icon: 'bx bx-user-plus' },
        { key: 5, label: 'Contracts', icon: 'bx bx-file' },
        { key: 6, label: 'Directory', icon: 'bx bx-globe' },
    ];

    const quickLinks = [
        { label: 'Add Facility', icon: 'bx bx-building-house', path: '/institution/add-clinic', color: ACCENT },
        { label: 'View Facilities', icon: 'bx bx-list-ul', path: '/institution/clinics', color: '#3b82f6' },
        { label: 'Institution', icon: 'bx bx-buildings', path: '/institution/clinics', color: '#8b5cf6' },
        { label: 'Qualifications', icon: 'bx bx-shield-quarter', path: '/institution/qualifications', color: '#d97706' },
    ];

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">

                        {/* Header */}
                        <div className="sc-header">
                            <div>
                                <h1><i className="bx bx-briefcase-alt me-2" style={{ color: ACCENT }}></i>Facility Manager</h1>
                                <p>Manage your facility operations, partners, services, and staff</p>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                {step !== 1 && (
                                    <button className="sc-btn sc-btn-ghost" onClick={() => setStep(1)}><i className="bx bx-arrow-back"></i> Back</button>
                                )}
                                <button className="sc-btn sc-btn-ghost" onClick={loadData}><i className="bx bx-refresh"></i> Refresh</button>
                                <Link to="/institution/add-clinic" className="sc-btn sc-btn-primary" style={{ textDecoration: 'none' }}><i className="bx bx-plus"></i> Add Facility</Link>
                            </div>
                        </div>

                        <div className="sc-card" style={{ margin: '0 0 16px' }}>
                            <div className="sc-card-body" style={{ padding: '12px 16px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {sections.map((section) => (
                                    <button
                                        key={section.key}
                                        className={step === section.key ? 'sc-btn sc-btn-primary sc-btn-sm' : 'sc-btn sc-btn-ghost sc-btn-sm'}
                                        onClick={() => setStep(section.key)}
                                    >
                                        <i className={section.icon}></i> {section.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="sc-stats" style={{ marginBottom: 20 }}>
                            <div className="sc-stat">
                                <div className="sc-stat-icon teal"><i className="bx bx-building-house"></i></div>
                                <div className="sc-stat-value">{switchableFacilities.length}</div>
                                <div className="sc-stat-label">Facilities</div>
                            </div>
                            <div className="sc-stat">
                                <div className="sc-stat-icon blue"><i className="bx bx-check-circle"></i></div>
                                <div className="sc-stat-value">{switchableFacilities.filter(f => f.active).length}</div>
                                <div className="sc-stat-label">Active</div>
                            </div>
                            <div className="sc-stat">
                                <div className="sc-stat-icon amber"><i className="bx bx-grid-alt"></i></div>
                                <div className="sc-stat-value">{modules.length}</div>
                                <div className="sc-stat-label">Modules</div>
                            </div>

                            {/* Current Facility – Clickable Switcher */}
                            <div className="sc-stat" ref={switcherRef} style={{ position: 'relative', cursor: 'pointer' }}
                                onClick={() => setShowSwitcher(!showSwitcher)}>
                                <div className="sc-stat-icon violet"><i className="bx bx-transfer-alt"></i></div>
                                <div className="sc-stat-value" style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    {clinicName || '—'}
                                    <i className={`bx bx-chevron-${showSwitcher ? 'up' : 'down'}`} style={{ fontSize: '0.9rem', color: 'var(--uzima-shell-text-muted)' }}></i>
                                </div>
                                <div className="sc-stat-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    Current
                                    {isInstitutionAdmin && <span style={{ padding: '1px 6px', borderRadius: 6, background: ACCENT_SOFT, color: ACCENT, fontSize: '0.55rem', fontWeight: 700 }}>Admin</span>}
                                </div>

                                {/* Dropdown */}
                                {showSwitcher && (
                                    <div style={{
                                        position: 'absolute', top: '100%', right: 0, marginTop: 8,
                                        width: 340, background: '#fff', borderRadius: 12,
                                        boxShadow: '0 12px 40px rgba(0,0,0,0.15)', border: '1px solid var(--uzima-shell-border)',
                                        zIndex: 999, overflow: 'hidden',
                                    }} onClick={e => e.stopPropagation()}>
                                        {/* Dropdown header */}
                                        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--uzima-shell-border)', background: 'rgba(15,118,110,0.03)' }}>
                                            <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 2 }}>
                                                <i className="bx bx-transfer-alt me-1" style={{ color: ACCENT }}></i>Switch Facility
                                            </div>
                                            <div style={{ fontSize: '0.68rem', color: 'var(--uzima-shell-text-muted)' }}>
                                                {isInstitutionAdmin ? `Institution Admin — ${institutionName}` : staffName}
                                                {' • '}{switchableFacilities.length} facilities available
                                            </div>
                                        </div>
                                        {/* Facility list */}
                                        <div style={{ maxHeight: 280, overflowY: 'auto', padding: '6px' }}>
                                            {switchableFacilities.length === 0 ? (
                                                <div style={{ padding: '20px 16px', textAlign: 'center', fontSize: '0.78rem', color: 'var(--uzima-shell-text-muted)' }}>
                                                    No other facilities available
                                                </div>
                                            ) : switchableFacilities.map(fac => (
                                                <div key={fac.id}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: 10,
                                                        padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                                                        background: fac.is_current ? ACCENT_SOFT : 'transparent',
                                                        border: fac.is_current ? `1px solid ${ACCENT}30` : '1px solid transparent',
                                                        transition: 'all 0.12s', marginBottom: 2,
                                                    }}
                                                    onMouseEnter={e => { if (!fac.is_current) (e.currentTarget as HTMLDivElement).style.background = 'rgba(15,23,42,0.03)'; }}
                                                    onMouseLeave={e => { if (!fac.is_current) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                                                    onClick={() => switchFacility(fac)}
                                                >
                                                    <div style={{
                                                        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                                                        background: fac.is_current ? ACCENT : 'rgba(15,23,42,0.06)',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}>
                                                        <i className={fac.is_current ? 'bx bx-check' : 'bx bx-building-house'}
                                                            style={{ fontSize: '0.9rem', color: fac.is_current ? '#fff' : 'var(--uzima-shell-text-muted)' }}></i>
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <span style={{ fontWeight: 600, fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {fac.name}
                                                            </span>
                                                            {fac.is_home_clinic && (
                                                                <span style={{ padding: '1px 5px', borderRadius: 4, background: 'rgba(59,130,246,0.1)', color: '#3b82f6', fontSize: '0.55rem', fontWeight: 700, flexShrink: 0 }}>HOME</span>
                                                            )}
                                                        </div>
                                                        <div style={{ fontSize: '0.66rem', color: 'var(--uzima-shell-text-muted)', display: 'flex', gap: 8 }}>
                                                            {fac.town && <span>{fac.town}</span>}
                                                            {fac.category && <span>• {fac.category}</span>}
                                                            <span>• {fac.staff_count} staff</span>
                                                        </div>
                                                    </div>
                                                    {fac.is_current && (
                                                        <span style={{ padding: '2px 8px', borderRadius: 8, background: ACCENT, color: '#fff', fontSize: '0.58rem', fontWeight: 700, flexShrink: 0 }}>ACTIVE</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        {/* Footer */}
                                        <div style={{ padding: '10px 16px', borderTop: '1px solid var(--uzima-shell-border)', background: 'rgba(15,23,42,0.01)' }}>
                                            <Link to="/institution/clinics" style={{ fontSize: '0.72rem', color: ACCENT, fontWeight: 700, textDecoration: 'none' }}
                                                onClick={() => setShowSwitcher(false)}>
                                                <i className="bx bx-cog me-1"></i>Manage All Facilities →
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Switching overlay */}
                        {isSwitching && (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '2rem', color: ACCENT }}></i>
                                <p style={{ marginTop: 10, fontWeight: 600, color: ACCENT }}>Switching facility...</p>
                            </div>
                        )}

                        {/* Main Launcher */}
                        {!isSwitching && step === 1 && (
                            <>
                                {/* Module Cards */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginBottom: 20 }}>
                                    {modules.map((mod) => (
                                        <div key={mod.key} className="sc-card" style={{ margin: 0, cursor: 'pointer', transition: 'all 0.18s' }}
                                            onClick={() => setStep(mod.key)}
                                            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLDivElement).style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; }}
                                            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.transform = ''; (e.currentTarget as HTMLDivElement).style.boxShadow = ''; }}
                                        >
                                            <div className="sc-card-body" style={{ padding: '24px 20px', textAlign: 'center' }}>
                                                <div style={{ width: 52, height: 52, borderRadius: 14, background: `${mod.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                                                    <i className={mod.icon} style={{ fontSize: '1.4rem', color: mod.color }}></i>
                                                </div>
                                                <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 4 }}>{mod.label}</div>
                                                <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)', lineHeight: 1.4 }}>{mod.desc}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Quick Links + Recent Facilities */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    {/* Quick Links */}
                                    <div className="sc-card" style={{ margin: 0 }}>
                                        <div style={{ padding: '12px 18px', borderBottom: `2px solid ${ACCENT}`, background: 'rgba(15,118,110,0.03)' }}>
                                            <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-rocket me-2" style={{ color: ACCENT }}></i>Quick Links</div>
                                        </div>
                                        <div className="sc-card-body">
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                                {quickLinks.map((item, i) => (
                                                    <Link key={i} to={item.path} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, textDecoration: 'none', background: `${item.color}08`, border: `1px solid ${item.color}20`, transition: 'all 0.15s' }}
                                                        onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = `${item.color}15`; }}
                                                        onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = `${item.color}08`; }}
                                                    >
                                                        <div style={{ width: 32, height: 32, borderRadius: 8, background: `${item.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <i className={item.icon} style={{ color: item.color, fontSize: '0.9rem' }}></i>
                                                        </div>
                                                        <span style={{ fontWeight: 600, fontSize: '0.78rem', color: 'var(--uzima-shell-text)' }}>{item.label}</span>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Switchable Facilities */}
                                    <div className="sc-card" style={{ margin: 0 }}>
                                        <div style={{ padding: '12px 18px', borderBottom: '2px solid #3b82f6', background: 'rgba(59,130,246,0.03)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-building-house me-2" style={{ color: '#3b82f6' }}></i>Facilities ({switchableFacilities.length})</div>
                                                <Link to="/institution/clinics" style={{ fontSize: '0.72rem', color: ACCENT, fontWeight: 700, textDecoration: 'none' }}>View All →</Link>
                                            </div>
                                        </div>
                                        <div className="sc-card-body">
                                            {isLoading ? (
                                                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                                    <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '1.4rem', color: ACCENT }}></i>
                                                    <p style={{ fontSize: '0.78rem', color: 'var(--uzima-shell-text-muted)', marginTop: 8 }}>Loading...</p>
                                                </div>
                                            ) : switchableFacilities.length === 0 ? (
                                                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                                    <p style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)' }}>No facilities registered yet.</p>
                                                    <Link to="/institution/add-clinic" className="sc-btn sc-btn-primary sc-btn-sm" style={{ textDecoration: 'none' }}><i className="bx bx-plus"></i> Add Facility</Link>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                    {switchableFacilities.slice(0, 5).map((f) => (
                                                        <div key={f.id} style={{
                                                            display: 'flex', alignItems: 'center', gap: 10,
                                                            padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                                                            background: f.is_current ? ACCENT_SOFT : 'rgba(15,23,42,0.02)',
                                                            border: f.is_current ? `1px solid ${ACCENT}25` : '1px solid transparent',
                                                            transition: 'all 0.12s',
                                                        }}
                                                            onMouseEnter={e => { if (!f.is_current) (e.currentTarget as HTMLDivElement).style.background = 'rgba(15,23,42,0.05)'; }}
                                                            onMouseLeave={e => { if (!f.is_current) (e.currentTarget as HTMLDivElement).style.background = 'rgba(15,23,42,0.02)'; }}
                                                            onClick={() => switchFacility(f)}
                                                        >
                                                            <div style={{ width: 34, height: 34, borderRadius: 10, background: f.is_current ? ACCENT : 'rgba(15,23,42,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                <i className={f.is_current ? 'bx bx-check' : 'bx bx-building-house'} style={{ color: f.is_current ? '#fff' : 'var(--uzima-shell-text-muted)', fontSize: '0.85rem' }}></i>
                                                            </div>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{ fontWeight: 600, fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                                                                <div style={{ fontSize: '0.66rem', color: 'var(--uzima-shell-text-muted)' }}>
                                                                    {f.town || f.email || 'Clinic'}{f.staff_count ? ` • ${f.staff_count} staff` : ''}
                                                                </div>
                                                            </div>
                                                            {f.is_current ? (
                                                                <span style={{ padding: '2px 8px', borderRadius: 12, background: ACCENT, color: '#fff', fontSize: '0.58rem', fontWeight: 700 }}>ACTIVE</span>
                                                            ) : (
                                                                <span style={{ padding: '2px 8px', borderRadius: 12, background: 'rgba(15,23,42,0.06)', color: 'var(--uzima-shell-text-muted)', fontSize: '0.58rem', fontWeight: 600 }}>Switch</span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {!isSwitching && step === 2 && <FacilityPartners />}

                        {!isSwitching && step === 3 && (
                            <div className="sc-card">
                                <div className="sc-card-body" style={{ textAlign: 'center', padding: '60px 20px' }}>
                                    <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(59,130,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                                        <i className="bx bx-briefcase-alt-2" style={{ fontSize: '1.5rem', color: '#3b82f6' }}></i>
                                    </div>
                                    <p style={{ fontWeight: 700 }}>Services</p>
                                    <p style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)' }}>Service management now lives in the service center so you can review categories, pricing, and availability without leaving the dashboard shell.</p>
                                    <Link to="/institution/services/my-services" className="sc-btn sc-btn-primary sc-btn-sm" style={{ textDecoration: 'none' }}><i className="bx bx-right-arrow-alt"></i> Open My Services</Link>
                                </div>
                            </div>
                        )}

                        {!isSwitching && step === 4 && <Staff />}

                        {!isSwitching && step === 5 && (
                            <div className="sc-card">
                                <div className="sc-card-body" style={{ textAlign: 'center', padding: '60px 20px' }}>
                                    <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(217,119,6,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                                        <i className="bx bx-file" style={{ fontSize: '1.5rem', color: '#d97706' }}></i>
                                    </div>
                                    <p style={{ fontWeight: 700 }}>Contracts</p>
                                    <p style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)' }}>Review your current agreements and contract activity from the dedicated contracts page.</p>
                                    <Link to="/institution/contracts" className="sc-btn sc-btn-primary sc-btn-sm" style={{ textDecoration: 'none' }}><i className="bx bx-right-arrow-alt"></i> Open Contracts</Link>
                                </div>
                            </div>
                        )}

                        {!isSwitching && step === 6 && (
                            <div className="sc-card">
                                <div className="sc-card-body" style={{ textAlign: 'center', padding: '60px 20px' }}>
                                    <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(225,29,72,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                                        <i className="bx bx-globe" style={{ fontSize: '1.5rem', color: '#e11d48' }}></i>
                                    </div>
                                    <p style={{ fontWeight: 700 }}>Partners Directory</p>
                                    <p style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)' }}>Browse, negotiate, and launch partner requests from the collaboration workspace.</p>
                                    <Link to="/institution/collaborations" className="sc-btn sc-btn-primary sc-btn-sm" style={{ textDecoration: 'none' }}><i className="bx bx-right-arrow-alt"></i> Collaboration Workspace</Link>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
                <DashboardFooter />
            </div>
            <Canvas />
        </>
    );
};

export default ClinicLauncher;
